import React, { useState } from 'react';
import { useAuthStore } from '../store/authStore';
import {
  DocumentTextIcon,
  CodeBracketIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  PlayIcon,
  ArrowTopRightOnSquareIcon,
  CubeIcon,
  CommandLineIcon,
  RocketLaunchIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

const Documentation: React.FC = () => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const { user } = useAuthStore();

  const copyToClipboard = async (text: string, codeId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(codeId);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const demoPages = [
    {
      id: 'basic-demo',
      title: '🎬 Temel Demo',
      description: 'Widget\'ın temel özelliklerini görün ve test edin',
      url: 'https://app.adinscene.com/demo/demo.html',
      icon: <PlayIcon className="h-6 w-6" />,
      features: ['Otomatik video algılama', 'İnteraktif kontroller', 'Debug modu', 'Test reklamları']
    },
    {
      id: 'videojs-demo',
      title: '📺 VideoJS Entegrasyonu',
      description: 'VideoJS player ile gelişmiş entegrasyon demo\'su',
      url: 'https://app.adinscene.com/demo/demo-videojs.html',
      icon: <CubeIcon className="h-6 w-6" />,
      features: ['VideoJS desteği', '4 farklı banner tipi', 'S3 JSON desteği', 'Gerçek zamanlı log\'lar']
    },
    {
      id: 'sdk-example',
      title: '⚙️ SDK Kullanım Rehberi',
      description: 'Adım adım SDK kullanımı ve API örnekleri',
      url: 'https://app.adinscene.com/demo/sdk-example.html',
      icon: <CommandLineIcon className="h-6 w-6" />,
      features: ['İnteraktif SDK testi', 'Kod örnekleri', 'API referansı', 'Widget yönetimi']
    }
  ];

  const quickStartCode = `<!-- 1. SDK'yı yükleyin -->
<script src="https://cdn.adinscene.com/sdk/adinscene.min.js"></script>

<!-- 2. SDK'yı başlatın -->
<script>
Adinscene.init({
    tenantId: '${user?.tenantId || 'YOUR_TENANT_ID'}',
    debug: true
}).then(() => {
    console.log('SDK başarıyla başlatıldı!');
}).catch(error => {
    console.error('SDK başlatma hatası:', error);
});
</script>`;

  const advancedCode = `// Gelişmiş konfigürasyon
Adinscene.init({
    tenantId: '${user?.tenantId || 'YOUR_TENANT_ID'}',
    apiBaseUrl: 'https://api.adinscene.com',
    debug: false,
    autoAttach: true,
    adDisplayDuration: 8000,
    videoSelectors: [
        'video',
        'video.vjs-tech',
        '.video-player video',
        '#custom-player video'
    ]
});

// Manuel widget ekleme
const videoElement = document.getElementById('my-video');
const widget = Adinscene.attachToVideo(videoElement);

// VideoJS desteği
const videojsPlayer = videojs('my-player');
Adinscene.attachToVideojsInstance(videojsPlayer);`;

  const bannerTypes = [
    {
      type: 'square',
      name: 'Square Banner',
      size: '300x300px',
      position: 'Sağ üst köşe',
      description: 'Kompakt ürün gösterimi için ideal',
      color: 'bg-blue-50 border-blue-200'
    },
    {
      type: 'lband',
      name: 'L-Band Banner',
      size: '400x200px',
      position: 'Sol üst köşe',
      description: 'Detaylı ürün bilgisi ile',
      color: 'bg-green-50 border-green-200'
    },
    {
      type: 'bottom',
      name: 'Alt Banner',
      size: '100%x100px',
      position: 'Alt orta',
      description: 'Minimal ve zarif tasarım',
      color: 'bg-purple-50 border-purple-200'
    },
    {
      type: 'fullscreen',
      name: 'Fullscreen Banner',
      size: '100%x100%',
      position: 'Tam ekran',
      description: 'Maksimum etki için tam ekran',
      color: 'bg-orange-50 border-orange-200'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4 dark:text-gray-300">
          adinscene SDK
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Video içeriklerinize interaktif alışveriş reklamları eklemek için geliştirilmiş modern JavaScript SDK'sı
        </p>
      </div>

      {/* Demo Pages */}
      <div className="bg-white shadow-sm rounded-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <RocketLaunchIcon className="h-6 w-6 mr-2 text-blue-600" />
          Canlı Demo'lar
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {demoPages.map((demo) => (
            <div key={demo.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-blue-100 rounded-lg mr-3">
                  {demo.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{demo.title}</h3>
              </div>
              <p className="text-gray-600 mb-4">{demo.description}</p>
              <ul className="space-y-1 mb-6">
                {demo.features.map((feature, index) => (
                  <li key={index} className="text-sm text-gray-500 flex items-center">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>
                    {feature}
                  </li>
                ))}
              </ul>
                             <a
                 href={demo.url}
                 target="_blank"
                 rel="noopener noreferrer"
                 className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
               >
                 Demo'yu Aç
                 <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-2" />
               </a>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Start */}
      <div className="bg-white shadow-sm rounded-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <LightBulbIcon className="h-6 w-6 mr-2 text-green-600" />
          Hızlı Başlangıç
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-blue-600">1</span>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">SDK'yı Yükleyin</h3>
            <p className="text-sm text-gray-600">Script tag'ini HTML'inize ekleyin</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-green-600">2</span>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Konfigüre Edin</h3>
            <p className="text-sm text-gray-600">Tenant ID ile SDK'yı başlatın</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-purple-600">3</span>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Test Edin</h3>
            <p className="text-sm text-gray-600">Demo ile widget'ı test edin</p>
          </div>
        </div>

        <div className="bg-gray-900 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-800">
            <span className="text-sm font-medium text-gray-300">Hızlı Başlangıç Kodu</span>
            <button
              onClick={() => copyToClipboard(quickStartCode, 'quick-start')}
              className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-300 hover:text-white"
            >
              {copiedCode === 'quick-start' ? (
                <>
                  <CheckIcon className="h-3 w-3 mr-1" />
                  Kopyalandı
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                  Kopyala
                </>
              )}
            </button>
          </div>
          <pre className="p-4 text-sm text-gray-100 overflow-x-auto">
            <code>{quickStartCode}</code>
          </pre>
        </div>
      </div>

      {/* Banner Types */}
      <div className="bg-white shadow-sm rounded-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          🎨 Banner Tipleri
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {bannerTypes.map((banner) => (
            <div key={banner.type} className={`border-2 rounded-lg p-4 ${banner.color}`}>
              <h3 className="font-semibold text-gray-900 mb-2">{banner.name}</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <p><strong>Boyut:</strong> {banner.size}</p>
                <p><strong>Konum:</strong> {banner.position}</p>
                <p className="text-xs mt-2">{banner.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Advanced Usage */}
      <div className="bg-white shadow-sm rounded-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <CodeBracketIcon className="h-6 w-6 mr-2 text-purple-600" />
          Gelişmiş Kullanım
        </h2>

        <div className="bg-gray-900 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-800">
            <span className="text-sm font-medium text-gray-300">Gelişmiş Konfigürasyon</span>
            <button
              onClick={() => copyToClipboard(advancedCode, 'advanced')}
              className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-300 hover:text-white"
            >
              {copiedCode === 'advanced' ? (
                <>
                  <CheckIcon className="h-3 w-3 mr-1" />
                  Kopyalandı
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                  Kopyala
                </>
              )}
            </button>
          </div>
          <pre className="p-4 text-sm text-gray-100 overflow-x-auto">
            <code>{advancedCode}</code>
          </pre>
        </div>
      </div>

      {/* Features Grid */}
      <div className="bg-white shadow-sm rounded-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          ✨ Özellikler
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="p-4 border border-gray-200 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">🎯 Akıllı Zamanlama</h3>
            <p className="text-sm text-gray-600">Reklamlar video içeriğine göre en uygun zamanlarda gösterilir</p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">📱 Responsive Tasarım</h3>
            <p className="text-sm text-gray-600">Tüm cihazlarda mükemmel görünüm ve kullanıcı deneyimi</p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">⚡ Hızlı Entegrasyon</h3>
            <p className="text-sm text-gray-600">Tek script ile kolayca sitenize entegre edilebilir</p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">📊 Detaylı Analitik</h3>
            <p className="text-sm text-gray-600">Reklam performansını gerçek zamanlı takip edin</p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">🎬 VideoJS Desteği</h3>
            <p className="text-sm text-gray-600">VideoJS player instance'larını doğrudan destekler</p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">🔒 Güvenli</h3>
            <p className="text-sm text-gray-600">HTTPS desteği ve güvenli API entegrasyonu</p>
          </div>
        </div>
      </div>

      {/* Resources */}
      <div className="bg-white shadow-sm rounded-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          📚 Kaynaklar
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Demo Dosyaları</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="https://app.adinscene.com/demo/demo-ads.json"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                >
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  demo-ads.json - Demo reklam verileri
                </a>
              </li>
              <li>
                <a
                                                          href="https://cdn.adinscene.com/sdk/adinscene.min.js"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                >
                  <CodeBracketIcon className="h-4 w-4 mr-2" />
                                                          adinscene.min.js - Minified SDK dosyası
                </a>
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Dokümantasyon</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="https://app.adinscene.com/demo/SDK-README.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                >
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  Kapsamlı SDK Dokümantasyonu
                </a>
              </li>
              <li>
                <a
                  href="https://app.adinscene.com/demo/DEMO-README.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                >
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  Demo Hazırlığı Rehberi
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Support */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h2 className="text-xl font-bold text-blue-900 mb-4">🆘 Destek</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="font-semibold text-blue-800 mb-2">Teknik Destek</h3>
            <p className="text-blue-700 text-sm">Email: support@adinscene.com</p>
          </div>
          <div>
            <h3 className="font-semibold text-blue-800 mb-2">API Endpoint</h3>
            <p className="text-blue-700 text-sm">Production: https://api.adinscene.com</p>
            <p className="text-blue-700 text-sm">Development: https://api-dev.adinscene.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;
